import React, { Key, useRef } from 'react';
import DialogInformationBox from '@components/core/dialog-information-box/dialog-information-box';
import { InfoBoxIcons } from '@components/core/dialog-information-box/dialog-information-box.interface';
import styles from './styles.module.scss';
import { useElementSize } from '@hooks-clean/index';
import { NullableStudy } from '@contexts/local-state-context';
import { useTranslation } from '@hooks/index';
import { requireStudy } from '@config/routes/page-data-helpers';

type Props = {
    routePath: string | undefined;
    study: NullableStudy | undefined;
    children?: React.ReactNode;
};

// *** Make sure to keep this synced with the "new UI" counterpart (./src/views-clean/components/core/page-banners-wrapper/) ***
/**
 * Adds 0 or more banners to the top of the page content based on conditions, such as:
 * - read-only study banner (if selected study is read-only)
 *
 * Sets a CSS property that tracks the height of the page banners container (`--page-banners-height`),
 *   so this can be accounted for by child layouts
 */
const PageBannersWrapper = ({ routePath, study, children }: Props) => {
    const { t } = useTranslation();

    // Watch height of banner (if included) and track its value (or 0) in a CSS variable
    const bannersContainerRef = useRef<HTMLDivElement>(null);
    // Ignore width so it doesn't rerender when only width changes
    const bannersContainerSize = useElementSize(bannersContainerRef, { trackWidth: false });

    // CSS variables are valid inline, but are missing on React.CSSProperties...
    const style = {
        '--page-banners-height': `${bannersContainerSize.height ?? 0}px`,
    } as React.CSSProperties;

    const routeInvolvesStudy = routePath != null && requireStudy(routePath);

    type PageBannerDefinition = {
        key: Key;
        icon: InfoBoxIcons;
        topText: string;
        bottomText?: string | undefined;
    };
    const banners: PageBannerDefinition[] = [];

    // If path involves study, and current study is read-only, show banner
    if (routeInvolvesStudy && study?.studyReadOnly === true) {
        banners.push({
            key: 'study-read-only',
            icon: InfoBoxIcons.infoCircle,
            topText: t('study:readOnlyBanner'),
        });
    }

    return (
        <div style={style}>
            {/* Add banners if any should be shown */}
            {banners.length > 0 && (
                <div ref={bannersContainerRef} className={styles.pageBannersContainer}>
                    {banners.map((b) => (
                        <DialogInformationBox key={b.key} icon={b.icon} topText={b.topText} bottomText={b.bottomText} />
                    ))}
                </div>
            )}

            {children}
        </div>
    );
};

export default PageBannersWrapper;
